/**
 * Runs — when the three agents run and what each run did.
 *
 * Every agent runs on a schedule (cron, edited through the agent registry like
 * every other agent on this page) and on demand: Run now covers all plans; to
 * run on chosen plans, select them in Plans. History lists every run with its
 * trigger, scope and counts, and refreshes while one is queued or running.
 * The trigger is shown as recorded: POST /runs is `manual`, a finding's Fix is
 * `fix`, and Run now here is `schedule` (the registry task takes no arguments).
 * Run again repeats a finished or stuck run on the same plans; a stuck run is
 * closed as failed and points at its replacement.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, RefreshCw, RotateCcw, Save } from 'lucide-react';
import { BUILDER_KEY, FINDER_KEY, HARNESS_KEY, funderAgentsApi, type AgentName, type FunderRun } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, RunBadge, duration, errText, fmtDate, type Notify } from './shared';

interface RegistryRow {
  key: string;
  display_name?: string;
  status?: string;
  cron_expr?: string;
  schedule_editable?: boolean;
  can_run_now?: boolean;
  /** The cron row's live on/off (TRUS-778); older registries omit it and the toggle shows on. */
  schedule_enabled?: boolean;
}

const AGENTS: { key: string; agent: AgentName; name: string; does: string; defaultSchedule: string }[] = [
  {
    key: FINDER_KEY,
    agent: 'finder',
    name: 'Source finder',
    does: 'Searches the web for the provider manual of each plan with no source; suggests what it finds for a person to accept.',
    defaultSchedule: 'Daily 08:00 UTC, up to 5 plans',
  },
  { key: BUILDER_KEY, agent: 'builder', name: 'Rules builder', does: 'Reads each enabled source, drafts rules from changed text, leaves them for approval.', defaultSchedule: 'Tuesdays 10:00 UTC' },
  { key: HARNESS_KEY, agent: 'harness', name: 'Interface harness', does: 'Checks rules and sources for conflicts, errors, staleness and denial patterns; opens findings.', defaultSchedule: 'daily 11:00 UTC' },
];

export const AGENT_NAMES: Record<AgentName, string> = { finder: 'Source finder', builder: 'Rules builder', harness: 'Interface harness' };

function runResult(r: FunderRun): string {
  const s = r.stats ?? {};
  if (r.agent === 'builder')
    return `${s.sources_fetched ?? 0} fetched · ${s.sources_unchanged ?? 0} unchanged · ${s.sources_failed ?? 0} failed · ${s.proposals_created ?? 0} drafts`;
  if (r.agent === 'finder')
    return `${s.plans_searched ?? 0} plans searched · ${s.suggested ?? 0} found · ${s.dropped ?? 0} dropped${s.errors ? ` · ${s.errors} errors` : ''}`;
  return `${s.findings_opened ?? 0} opened · ${s.findings_resolved ?? 0} resolved`;
}

function ScheduleCard({ row, def, notify, onRun }: { row: RegistryRow | undefined; def: (typeof AGENTS)[number]; notify: Notify; onRun: () => void }) {
  const [cron, setCron] = useState(row?.cron_expr ?? '');
  const [enabled, setEnabled] = useState<boolean | undefined>(row?.schedule_enabled);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    setCron(row?.cron_expr ?? '');
    setEnabled(row?.schedule_enabled);
  }, [row?.cron_expr, row?.schedule_enabled]);

  const cronOk = cron.trim().split(/\s+/).length === 5;
  const save = async (patch: { cron_expr?: string; enabled?: boolean }) => {
    setBusy('save');
    try {
      const res = await funderAgentsApi.setSchedule(def.key, patch);
      if (res) {
        setCron(res.cron_expr ?? cron);
        setEnabled(res.enabled);
      }
      notify(true, `${def.name} schedule saved.`);
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    if (!window.confirm(`Run the ${def.name.toLowerCase()} on all plans now?\n\n${def.does}`)) return;
    setBusy('run');
    try {
      await funderAgentsApi.runNow(def.key);
      notify(true, `${def.name} started on all plans.`);
      onRun();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-md border border-v2border bg-surface p-4 space-y-3">
      <div>
        <p className="font-semibold text-ink">{row?.display_name || def.name}</p>
        <p className="text-xs text-ink-500">{def.does}</p>
        {row?.cron_expr && <p className="mt-1 text-xs text-ink-500">Default: {def.defaultSchedule}.</p>}
        {!row && <p className="mt-1 text-xs text-attention-ink">Not in the agent registry yet — deploy the backend for this tab.</p>}
        {row?.status === 'paused' && <p className="mt-1 text-xs text-attention-ink">Paused in the Agent catalog.</p>}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-semibold text-ink-700">
          Schedule (cron, UTC)
          <input
            value={cron}
            onChange={(e) => setCron(e.target.value)}
            disabled={!row?.schedule_editable}
            placeholder="0 10 * * 2"
            className="mt-1 block w-40 rounded-md border border-v2border bg-surface px-2 py-1.5 font-mono text-sm text-ink disabled:opacity-60"
          />
        </label>
        <Btn disabled={!row?.schedule_editable || !cronOk || cron.trim() === (row?.cron_expr ?? '')} busy={busy === 'save'} onClick={() => void save({ cron_expr: cron.trim() })}>
          <Save className="w-3.5 h-3.5" aria-hidden="true" />
          Save
        </Btn>
        <label className="inline-flex items-center gap-1 pb-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={enabled ?? true}
            disabled={!row?.schedule_editable || busy === 'save'}
            onChange={(e) => void save({ enabled: e.target.checked })}
          />
          Runs on schedule{enabled === undefined ? '' : enabled ? '' : ' (off)'}
        </label>
        <span className="ml-auto">
          <Btn tone="primary" disabled={!row?.can_run_now} busy={busy === 'run'} onClick={() => void runNow()}>
            <Play className="w-3.5 h-3.5" aria-hidden="true" />
            Run now — all plans
          </Btn>
        </span>
      </div>
    </div>
  );
}

export default function RunsView({ notify, refreshKey }: { notify: Notify; refreshKey: number }) {
  const [registry, setRegistry] = useState<RegistryRow[]>([]);
  const [runs, setRuns] = useState<FunderRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<AgentName | ''>('');
  const timer = useRef<number | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);

  const loadRuns = useCallback(async () => {
    try {
      setRuns(await funderAgentsApi.runs(agentFilter || undefined, 40));
      setError(null);
    } catch (e) {
      setError(errText(e));
      setRuns((r) => r ?? []);
    }
  }, [agentFilter]);

  useEffect(() => {
    funderAgentsApi
      .registry()
      .then((rows) => setRegistry(rows as RegistryRow[]))
      .catch((e) => notify(false, errText(e)));
  }, [notify]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns, refreshKey]);

  const retry = async (r: FunderRun) => {
    const scope = r.payer_ids?.length ? `${r.payer_ids.length} plan${r.payer_ids.length === 1 ? '' : 's'}` : 'all plans';
    const name = AGENT_NAMES[r.agent].toLowerCase();
    const note = r.stale ? `\n\nRun #${r.id} has made no progress and will be closed as failed.` : '';
    if (!window.confirm(`Run the ${name} again on ${scope}?${note}`)) return;
    setRetrying(r.id);
    try {
      const run = await funderAgentsApi.retryRun(r.id);
      notify(true, `Run #${run?.id ?? ''} queued (again of #${r.id}).`);
      await loadRuns();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setRetrying(null);
    }
  };

  // Poll while anything is queued or running (a stuck run will not move, so it does not count).
  const active = (runs ?? []).some((r) => (r.status === 'queued' || r.status === 'running') && !r.stale);
  useEffect(() => {
    if (!active) return;
    timer.current = window.setInterval(() => void loadRuns(), 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [active, loadRuns]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {AGENTS.map((def) => (
          <ScheduleCard key={def.key} def={def} row={registry.find((r) => r.key === def.key)} notify={notify} onRun={() => void loadRuns()} />
        ))}
      </div>

      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-ink">Run history</h4>
        <select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value as AgentName | '')} className="rounded-md border border-v2border bg-surface px-2 py-1 text-sm text-ink">
          <option value="">All agents</option>
          <option value="finder">Source finder</option>
          <option value="builder">Rules builder</option>
          <option value="harness">Interface harness</option>
        </select>
        {active && <span className="text-xs text-v2accent-ink">Refreshing while a run is in progress…</span>}
        <span className="ml-auto">
          <Btn tone="ghost" title="Refresh" onClick={() => void loadRuns()}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
          </Btn>
        </span>
      </div>
      <ErrorLine text={error} />
      {runs === null ? (
        <p className="text-sm text-ink-500">Loading runs…</p>
      ) : runs.length === 0 ? (
        <Empty>No runs yet. Run an agent now, or select plans in Plans.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-md border border-v2border bg-surface">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-v2border text-left text-xs text-ink-500">
                <th className="p-2">Run</th>
                <th className="p-2">Agent</th>
                <th className="p-2">Trigger</th>
                <th className="p-2">Scope</th>
                <th className="p-2">Result</th>
                <th className="p-2">Status</th>
                <th className="p-2">Started</th>
                <th className="p-2">Took</th>
                <th className="p-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const result = runResult(r);
                return (
                  <tr key={r.id} className="border-b border-v2border last:border-0 align-top">
                    <td className="p-2 tabular-nums text-ink-500">#{r.id}</td>
                    <td className="p-2 text-ink">{AGENT_NAMES[r.agent] ?? r.agent}</td>
                    <td className="p-2 text-ink-700"><code className="text-xs">{r.trigger}</code></td>
                    <td className="p-2 text-ink-700">
                      {r.payer_ids?.length ? `${r.payer_ids.length} plan${r.payer_ids.length === 1 ? '' : 's'}` : 'All plans'}
                      {r.force ? ' · forced' : ''}
                    </td>
                    <td className="p-2 text-ink-700">
                      {result}
                      {r.error && <div className="text-xs text-danger-ink">{r.error}</div>}
                    </td>
                    <td className="p-2">
                      <RunBadge status={r.status} />
                      {r.stale && <div className="mt-1 text-xs text-attention-ink">Stuck — no progress</div>}
                      {r.stats?.retry_of && <div className="mt-1 text-xs text-ink-500">Again of #{r.stats.retry_of}</div>}
                    </td>
                    <td className="p-2 text-ink-500">{fmtDate(r.started_at || r.created_at)}</td>
                    <td className="p-2 text-ink-500">{duration(r.started_at, r.finished_at)}</td>
                    <td className="p-2 text-right">
                      {(r.stale || r.status === 'failed' || r.status === 'completed') && (
                        <Btn tone={r.stale || r.status === 'failed' ? 'primary' : 'ghost'} busy={retrying === r.id} onClick={() => void retry(r)}>
                          <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                          Run again
                        </Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
